import React, { useRef, useEffect, useState, act } from 'react';
import logo from './logo.svg';
import './App.css';
import { useMobileDetect } from './hooks/useMobileDetect';

const GridCanvas = ({label= null, rowPercentage = 5, colPercentage = 5, diffPositions = [], foundMatch= [],  backgroundImage, processSelectedPosition }) => {
  const canvasRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState({ width: 400, height: 400 });
  const [circlePosition, setCirclePosition] = useState(null);

  // Calculate the number of rows and columns based on the canvas size and percentage
  const rows = 100 / rowPercentage;
  const cols = 100 / colPercentage;
  const blockSize = {
    width: canvasSize.width / cols,
    height: canvasSize.height / rows,
  };

  // Draw the grid, circles, and background image (if there is one) on the canvas
  const drawGrid = (ctx) => {
    // Clear the canvas
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

    

    // Draw the background image if provided
    if (backgroundImage) {
      const img = new Image();
      img.src = backgroundImage;
      img.onload = () => {
        ctx.drawImage(img, 0, 0, canvasSize.width, canvasSize.height);

        // After drawing the background, proceed with the grid and circles
        drawGridAndCircles(ctx);

        if(foundMatch && Array.isArray(foundMatch))
          {
            for (let position of foundMatch) {       
              drawMatchCircles(ctx, position.x, position.y, position.r)
            }
          }
      };
    } else {
      // If no background image, just draw the grid and circles
      drawGridAndCircles(ctx);
    }

    if(foundMatch && Array.isArray(foundMatch))
    {
      for (let position of foundMatch) {       
        drawMatchCircles(ctx, position.x, position.y)
      }
    }
  };
  

  // Helper function to draw the grid and the circles
  const drawGridAndCircles = (ctx) => {
    // Set the stroke style for the grid lines
    ctx.strokeStyle = '#ccc';
    ctx.lineWidth = 1;

    // Draw the grid
    for (let row = 0; row <= rows; row++) {
      ctx.beginPath();
      ctx.moveTo(0, row * blockSize.height);
      ctx.lineTo(canvasSize.width, row * blockSize.height);
      ctx.stroke();
    }

    for (let col = 0; col <= cols; col++) {
      ctx.beginPath();
      ctx.moveTo(col * blockSize.width, 0);
      ctx.lineTo(col * blockSize.width, canvasSize.height);
      ctx.stroke();
    }

    // Draw the circles if a position is defined
    if (circlePosition) {
      const { x, y } = circlePosition;

      // Red-filled circle (33% of block size)
      ctx.beginPath();
      ctx.arc(
        x * blockSize.width + blockSize.width / 2,
        y * blockSize.height + blockSize.height / 2,
        Math.min(blockSize.width, blockSize.height) / 3,
        0,
        2 * Math.PI
      );
      ctx.fillStyle = 'red';
      ctx.fill();

      // Transparent circle with green border (20% of block size)
      ctx.beginPath();
      ctx.arc(
        x * blockSize.width + blockSize.width / 2,
        y * blockSize.height + blockSize.height / 2,
        Math.min(blockSize.width, blockSize.height) * 0.2,
        0,
        2 * Math.PI
      );
      ctx.strokeStyle = 'green';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.fillStyle = 'rgba(0, 255, 0, 0)'; // Transparent fill
      ctx.fill();
    }
  };

   // Helper function to draw the grid and the circles
   const drawMatchCircles = (ctx, x, y, r = 1) => {
    // Set the stroke style for the grid lines
    ctx.strokeStyle = '#ccc';
    ctx.lineWidth = 1;
    
    // Draw the circles if a position is defined
    if (true) {
      console.log("pos",{x,y})
      // Transparent circle with green border (20% of block size)
      ctx.beginPath();
      ctx.arc(
        x * blockSize.width + blockSize.width / 2,
        y * blockSize.height + blockSize.height / 2,
        Math.min(blockSize.width, blockSize.height) * 0.8*r,
        0,
        2 * Math.PI
      );
      ctx.strokeStyle = 'rgb(3 101 117)';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.fillStyle = 'rgba(0, 25, 200, 0.3)'; // Transparent fill
      ctx.fill();
    }
  };

  // Handle the user's click on the canvas
  const handleCanvasClick = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Calculate the grid block the user clicked on
    const clickedX = Math.floor(x / blockSize.width);
    const clickedY = Math.floor(y / blockSize.height);

    // Set the circle position to the clicked block
    setCirclePosition({ x: clickedX, y: clickedY });

    processSelectedPosition(clickedX, clickedY)
  };

  // Redraw the canvas when the component mounts or updates
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    drawGrid(ctx);
    console.log(circlePosition)
  }, [circlePosition, canvasSize, backgroundImage, foundMatch]); // Redraw whenever the circle position, canvas size, or background image changes

  // Handle resizing of the canvas to make it responsive
  useEffect(() => {
    const resizeCanvas = () => {
      const canvas = canvasRef.current;
      setCanvasSize({ width: canvas.clientWidth, height: canvas.clientHeight });
    };

    resizeCanvas(); // Initial size setting
    window.addEventListener('resize', resizeCanvas); // Listen for window resize events

    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  return (
    <div style={{display:'flex', padding:10, position:'relative', flexDirection:'column'}}>
      {label && <span style={{color:'#9E9E9E', fontWeight:'bold'}}>{label}</span>}
    <canvas
      ref={canvasRef}
      width={canvasSize.width}
      height={canvasSize.height}
      style={{ border: '1px dotted #CCCCCC', aspectRatio:1, margin:0, borderRadius:10, width: '100%', height: '100%', cursor: 'pointer' }}
      onClick={handleCanvasClick}
    />
    </div>
  );
};

const GridCanvasV3 = ({ rowPercentage = 10, colPercentage = 10 }) => {
  const canvasRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState({ width: 500, height: 500 });
  const [circlePosition, setCirclePosition] = useState(null);

  // Calculate the number of rows and columns based on the canvas size and percentage
  const rows = 100 / rowPercentage;
  const cols = 100 / colPercentage;
  const blockSize = {
    width: canvasSize.width / cols,
    height: canvasSize.height / rows,
  };

  // Draw the grid and the circle (if there is one) on the canvas
  const drawGrid = (ctx) => {
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    
    // Set the stroke style for the grid lines
    ctx.strokeStyle = '#ccc';
    ctx.lineWidth = 1;

    // Draw the grid
    for (let row = 0; row <= rows; row++) {
      ctx.beginPath();
      ctx.moveTo(0, row * blockSize.height);
      ctx.lineTo(canvasSize.width, row * blockSize.height);
      ctx.stroke();
    }

    for (let col = 0; col <= cols; col++) {
      ctx.beginPath();
      ctx.moveTo(col * blockSize.width, 0);
      ctx.lineTo(col * blockSize.width, canvasSize.height);
      ctx.stroke();
    }

    // Draw the circles if a position is defined
    if (circlePosition) {
      const { x, y } = circlePosition;

      // Red-filled circle (33% of block size)
      ctx.beginPath();
      ctx.arc(
        x * blockSize.width + blockSize.width / 2, // X coordinate of the circle's center
        y * blockSize.height + blockSize.height / 2, // Y coordinate of the circle's center
        Math.min(blockSize.width, blockSize.height) / 3, // Circle radius
        0,
        2 * Math.PI
      );
      ctx.fillStyle = 'red';
      ctx.fill();

      // Transparent circle with green border (20% of block size)
      ctx.beginPath();
      ctx.arc(
        x * blockSize.width + blockSize.width / 2, // X coordinate of the circle's center
        y * blockSize.height + blockSize.height / 2, // Y coordinate of the circle's center
        Math.min(blockSize.width, blockSize.height) * 1.0, // Circle radius (20% of block size)
        0,
        2 * Math.PI
      );
      ctx.strokeStyle = 'green';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.fillStyle = 'rgba(0, 255, 0, 0)'; // Transparent fill
      ctx.fill();
    }
  };

  // Handle the user's click on the canvas
  const handleCanvasClick = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Calculate the grid block the user clicked on
    const clickedX = Math.floor(x / blockSize.width);
    const clickedY = Math.floor(y / blockSize.height);

    // Set the circle position to the clicked block
    setCirclePosition({ x: clickedX, y: clickedY });
  };

  // Redraw the canvas when the component mounts or updates
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    drawGrid(ctx);
  }, [circlePosition, canvasSize]); // Redraw whenever the circle position or canvas size changes

  // Handle resizing of the canvas to make it responsive
  useEffect(() => {
    const resizeCanvas = () => {
      const canvas = canvasRef.current;
      setCanvasSize({ width: canvas.clientWidth, height: canvas.clientHeight });
    };

    resizeCanvas(); // Initial size setting
    window.addEventListener('resize', resizeCanvas); // Listen for window resize events

    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  return (
    <div style={{display:'flex', flexDirection:'column'}}>
      <span style={{color:'#9E9E9E', fontWeight:'bold'}}>A</span>
      <canvas
      ref={canvasRef}
      width={canvasSize.width}
      height={canvasSize.height}
      style={{ border: '1px solid black', width: '100%', height: '100%', cursor: 'pointer' }}
      onClick={handleCanvasClick}
    />
    </div>
    
  );
};



const GridCanvasV2 = ({ rowPercentage = 5, colPercentage = 5 }) => {
  const canvasRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState({ width: 500, height: 500 });
  const [circlePosition, setCirclePosition] = useState(null);

  // Calculate the number of rows and columns based on the canvas size and percentage
  const rows = 100 / rowPercentage;
  const cols = 100 / colPercentage;
  const blockSize = {
    width: canvasSize.width / cols,
    height: canvasSize.height / rows,
  };

  // Draw the grid and the circle (if there is one) on the canvas
  const drawGrid = (ctx) => {
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    
    // Set the stroke style for the grid lines
    ctx.strokeStyle = '#ccc';
    ctx.lineWidth = 1;

    // Draw the grid
    for (let row = 0; row <= rows; row++) {
      ctx.beginPath();
      ctx.moveTo(0, row * blockSize.height);
      ctx.lineTo(canvasSize.width, row * blockSize.height);
      ctx.stroke();
    }

    for (let col = 0; col <= cols; col++) {
      ctx.beginPath();
      ctx.moveTo(col * blockSize.width, 0);
      ctx.lineTo(col * blockSize.width, canvasSize.height);
      ctx.stroke();
    }

    // Draw the circle if a position is defined
    if (circlePosition) {
      const { x, y } = circlePosition;
      ctx.beginPath();
      ctx.arc(
        x * blockSize.width + blockSize.width / 2, // X coordinate of the circle's center
        y * blockSize.height + blockSize.height / 2, // Y coordinate of the circle's center
        Math.min(blockSize.width, blockSize.height) / 3, // Circle radius based on smallest block dimension
        0,
        2 * Math.PI
      );
      ctx.fillStyle = 'red';
      ctx.fill();
    }
  };

  // Handle the user's click on the canvas
  const handleCanvasClick = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Calculate the grid block the user clicked on
    const clickedX = Math.floor(x / blockSize.width);
    const clickedY = Math.floor(y / blockSize.height);

    // Set the circle position to the clicked block
    setCirclePosition({ x: clickedX, y: clickedY });
  };

  // Redraw the canvas when the component mounts or updates
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    drawGrid(ctx);
  }, [circlePosition, canvasSize]); // Redraw whenever the circle position or canvas size changes

  // Handle resizing of the canvas to make it responsive
  useEffect(() => {
    const resizeCanvas = () => {
      const canvas = canvasRef.current;
      setCanvasSize({ width: canvas.clientWidth, height: canvas.clientHeight });
    };

    resizeCanvas(); // Initial size setting
    window.addEventListener('resize', resizeCanvas); // Listen for window resize events

    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      width={canvasSize.width}
      height={canvasSize.height}
      style={{ border: '1px solid black', width: '100%', height: '100%', cursor: 'pointer' }}
      onClick={handleCanvasClick}
    />
  );
};

const GridCanvasV1 = ({ rows = 20, cols = 20, blockSize = 50 }) => {
  const canvasRef = useRef(null);
  const [circlePosition, setCirclePosition] = useState(null);

  // Draw the grid and the circle (if there is one) on the canvas
  const drawGrid = (ctx) => {
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    
    // Set the stroke style for the grid lines
    ctx.strokeStyle = '#ccc';
    ctx.lineWidth = 1;

    // Draw the grid
    for (let row = 0; row <= rows; row++) {
      ctx.beginPath();
      ctx.moveTo(0, row * blockSize);
      ctx.lineTo(cols * blockSize, row * blockSize);
      ctx.stroke();
    }

    for (let col = 0; col <= cols; col++) {
      ctx.beginPath();
      ctx.moveTo(col * blockSize, 0);
      ctx.lineTo(col * blockSize, rows * blockSize);
      ctx.stroke();
    }

    // Draw the circle if a position is defined
    if (circlePosition) {
      const { x, y } = circlePosition;
      ctx.beginPath();
      ctx.arc(
        x * blockSize + blockSize / 2, // X coordinate of the circle's center
        y * blockSize + blockSize / 2, // Y coordinate of the circle's center
        blockSize / 3, // Circle radius
        0,
        2 * Math.PI
      );
      ctx.fillStyle = 'red';
      ctx.fill();
    }
  };

  // Handle the user's click on the canvas
  const handleCanvasClick = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Calculate the grid block the user clicked on
    const clickedX = Math.floor(x / blockSize);
    const clickedY = Math.floor(y / blockSize);

    // Set the circle position to the clicked block
    setCirclePosition({ x: clickedX, y: clickedY });
  };

  // Redraw the canvas when the component mounts or updates
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    drawGrid(ctx);
  }, [circlePosition]); // Redraw whenever the circle position changes

  return (
    <canvas
      ref={canvasRef}
      width={cols * blockSize}
      height={rows * blockSize}
      style={{ border: '1px solid black', cursor: 'pointer' }}
      onClick={handleCanvasClick}
    />
  );
};

function App() {

  const gameListData = [
    {
      coverImage: "https://s3.us-west-2.amazonaws.com/media.braintrainer.live/spot-the-difference/001-image-1.jpg",
      title: "Street View",
      level:1,
      id:1
    },
    {
      coverImage: "https://s3.us-west-2.amazonaws.com/media.braintrainer.live/spot-the-difference/001-image-1.jpg",
      title: "Street Light Delight",
      level:2,
      id:2
    },
    {
      coverImage: "https://s3.us-west-2.amazonaws.com/media.braintrainer.live/spot-the-difference/001-image-1.jpg",
      title: "Zebra Crossing",
      level:3,
      id:3
    }
  ]

  const data = 
  {
    image1:"https://s3.us-west-2.amazonaws.com/media.braintrainer.live/spot-the-difference/001-image-1.jpg",
    image2:"https://s3.us-west-2.amazonaws.com/media.braintrainer.live/spot-the-difference/001-image-2.jpg",
    matchPositions:[{id:1,x:2,y:5, r:4, related:[{x:1,y:5}]} 
    ,{id:2,x:5,y:5, r:2, related:[{x:4,y:5}]}]
  }

  const {isMobile} = useMobileDetect();
  const [activeLevel, setActiveLevel] = useState(null)
  const [levels, setLevels] = useState([...gameListData])
  const [foundMatches, setFoundMatches] = useState([])
  const [lives, setLives] = useState(500)
  const [points, setPoints] = useState(0)
  // 0 - Playing, 1 - Win, 2 - Game Over
  const [gameStatus, setGameStatus] = useState(-1)

  function findMatch(x, y, matchPositions) {
    // Iterate over matchPositions array
    for (let position of matchPositions) {
      // Check if the x and y match the main object's x and y
      if (position.x === x && position.y === y) {
        return { id: position.id, x: position.x, y: position.y, r: position.r };
      }
  
      // If not matched, check the related array
      if (position.related && Array.isArray(position.related)) {
        for (let relatedPos of position.related) {
          if (relatedPos.x === x && relatedPos.y === y) {
            return { id: position.id, x: position.x, y: position.y, r: position.r };
          }
        }
      }
    }
  
    // Return null if no match is found
    return null;
  }

  // 1. When a position is clicked, the x,y coordinates are passed to the 
  // below callback
  const processSelectedPosition = (x, y) => {
    var foundMatch = findMatch(x,y,data.matchPositions);

    console.log("F",{foundMatch, x,y})

    if(foundMatch)
    {
      console.log(foundMatch)
      // If found match is not already in foundmatch array, then add point
      if(!foundMatches.find(fm => fm.id == foundMatch.id))
      {
          // add point
          setPoints(p => p + 1)
          // add match
          setFoundMatches(fm => [...fm,{...foundMatch}])
      }
      
      
    }
    else
    {
      // else penalize
      setLives(l => l -1)
    }
  }
  // WHen a match is found the found match is populated, and points are added

  // When noot found, some deductions are made
  useEffect(()=>{

    if(points == data?.matchPositions?.length)
    {
      setGameStatus(1)

      if(activeLevel)
      {
        localStorage.setItem("passedLevel", activeLevel?.level)
      }
    }

    if(lives <= 0)
    {
      setGameStatus(2)
    }
    
  },[points, lives])


  console.log(findMatch(1,5,data.matchPositions))

  const retartCurrentGame = () =>
  {
    setGameStatus(0)
    setLives(5)
    setPoints(0)
  }

  const isLastLevel = () => 
  {
    return (activeLevel?.level >= levels.length)
  }

  const progressToNextLevel = () => 
  {    
    if(!isLastLevel())
    {
      console.log(activeLevel.level)
      setActiveLevel(levels[activeLevel.level])
    }
    setGameStatus(0)
    setLives(5)
    setPoints(0)
    setFoundMatches([])
  }

  const startLevel = (levelData) => {
    setActiveLevel(levelData)
    setGameStatus(0)
    setLives(5)
    setPoints(0)
    setFoundMatches([])
  }

  const hasPassedLevel = (levelNumber) => {
    var passedLevel = localStorage.getItem("passedLevel")

    if(passedLevel)
    {
      return parseInt(passedLevel) + 1 >= levelNumber
    }

    return 1 >= levelNumber;
  }
  
  return (
    <div className="App" style={{padding:0, maxWidth:'100%'}}>
      {gameStatus === -1 && 
      <>
        {/** Home */}
        <div style={{height:80, width:'100%', fontSize:30, padding:10, fontFamily:'Dancing Script'}}>
          Spot the difference!
        </div>
        <div style={{display:"inline-grid", width:"100%", padding:10, gridTemplateColumns:"repeat(auto-fill, minmax(160px, 1fr))"}}>
          {levels.map(gl => 
          <div style={{display:'flex', justifyContent:'center'}}>
          <div onClick={()=>hasPassedLevel(gl.level)?startLevel(gl):null} style={{cursor:'pointer', width:100, minHeight:100, display:'flex', /*justifyContent:'center',*/ alignItems:'center', flexDirection:'column', position:'relative'}}>
            <div style={{width:140, height:140, borderRadius:10, aspectRatio:1, filter: hasPassedLevel(gl.level)? null : "grayscale(100%) blur(1px)", backgroundSize: 'contain', backgroundImage:`url('${gl.coverImage}')`}} src={gl.coverImage}/>
            {!hasPassedLevel(gl.level) && <img width={48} height={48} style={{position:'absolute', top:'50%', left:'50%', transform:'translate(-50%, -50%)'}} src={"icons/lock-icon.png"}/>}
            <span style={{fontSize:11, color:'grey', margin:15}}>{gl.title}</span>
          </div>
          </div>)}
        </div>
      </>}
      {gameStatus === 1 && 
      <div style={{display:'flex', flexDirection:'column'}}>
        <span style={{position:"absolute", right:30, top:30, cursor:'pointer'}} onClick={()=>setGameStatus(-1)}>Exit</span>      
        
        <span>You've won</span>
        <div>
          {!isLastLevel() && <button onClick={()=>progressToNextLevel()}>Go to next</button>}
        </div>
        </div>}
      {gameStatus === 2 && <>        
        <div style={{display:'flex', flexDirection:'column'}}>
        <span style={{position:"absolute", right:30, top:30, cursor:'pointer'}} onClick={()=>setGameStatus(-1)}>Exit</span>      
        
        <span>You've lost</span>
        <div><button onClick={()=>retartCurrentGame()}>Try again</button></div>
        </div>
      </>}
      {gameStatus === 0 && <header className="App-header">  
        <span style={{position:"absolute", right:30, top:30, cursor:'pointer'}} onClick={()=>setGameStatus(-1)}>Exit</span>      
        <div style={{display:"flex", alignItems:"center"}}>
          <div style={{padding:15, display:'flex', flexDirection:'column'}}>
          <span style={{fontWeight:'bold'}}>{activeLevel?.title}</span>
          <span style={{fontWeight:'normal', fontSize:12, color:'#575757'}}>Level: {activeLevel?.level}</span>
          </div>           
        </div>    
        <div style={{display:"flex", alignItems:"center"}}>          
          <div style={{display:"flex", alignItems:"center"}}>
            <div style={{padding:15}}>
            <span style={{fontWeight:'bold'}}>{points}</span>/<span style={{fontSize:13, color:'#575757'}}>{data?.matchPositions?.length}</span>
            </div>
            <img width={32} height={32} src="icons/points-icon.png"/>
          </div>        
          <div style={{display:"flex", alignItems:"center"}}>
            <div style={{padding:15}}>
            <span style={{fontWeight:'bold'}}>{lives}</span>/<span style={{fontSize:13, color:'#575757'}}>5</span>
            </div>
            <img width={32} height={32} src="icons/live-icon.png"/>
          </div>
        </div>
        <div style={{display:'flex', padding:15, flexDirection: isMobile ? 'column' : 'row' }}>
          
          <GridCanvas label={"A"} key={"image-1"} backgroundImage={data.image1} processSelectedPosition={processSelectedPosition} foundMatch = {foundMatches}/>
          
          
          <GridCanvas label={"B"}  key={"image-2"} backgroundImage={data.image2} processSelectedPosition={processSelectedPosition} foundMatch = {foundMatches}/>
                   
        </div>
        </header>}
    </div>
  );
}

export default App;
